import {NetworkStatus, useMutation, useQuery} from '@apollo/client'
import {
  Button,
  Checkbox,
  CircularProgress,
  debounce,
  DialogActions,
  DialogContent,
  DialogTitle,
  OutlinedInput,
  TextField,
  Typography,
} from '@mui/material'
import {makeStyles} from '@mui/styles'
import gql from 'graphql-tag'
import {useCallback, useContext, useEffect, useMemo, useState} from 'react'
import {useGetIdentity, useNotify, useTranslate} from 'react-admin'
import {useNavigate} from 'react-router'

import ApiUrlsContext from '../../contexts/ApiUrlsContext'
import {useUserDisplayName} from '../../hooks/useUserDisplayName'
import {
  MutationRoot,
  QueryRoot,
} from '../../types/graphqlSchema'
import DialogCloseButton from '../DialogCloseButton'
import ExtendedDialog from '../ExtendedDialog'
import {SearchIcon} from '../icons'
import {ChatContext} from './ChatContext'

const ForwardChatMessagesDialog = ({
  onClose,
  open,
}: ForwardChatMessagesDialogProps) => {
  const styles = useStyles()
  const notify = useNotify()
  const translate = useTranslate()
  const navigate = useNavigate()
  const userDisplayName = useUserDisplayName()
  const {getFilesApiUrl} = useContext(ApiUrlsContext)
  const {
    channelId,
    onUnsetBulkAction,
    selectedChatMessages,
  } = useContext(ChatContext)
  const {identity: {id: userId} = {}} = useGetIdentity()
  const [filter, setFilter] = useState<string>('')
  const {
    data: {chats: internalChats = []} = {},
    loading: isLoadingInternalChats,
    networkStatus: internalChatsQueryNetworkStatus,
    refetch: refetchInternalChats,
  } = useQuery<QueryRoot['chats']>(
    INTERNAL_CHATS_QUERY, {
      skip: !(open && userId),
      variables: {filter: '%%', userId},
    },
  )
  const {
    data: {merchant_customer_users: merchantCustomerUsers = []} = {},
    loading: isLoadingMerchantCustomerUsers,
    networkStatus: merchantCustomerUsersQueryNetworkStatus,
    refetch: refetchMerchantCustomerUsers,
  } = useQuery<QueryRoot['merchant_customer_users']>(
    MERCHANT_CUSTOMER_USERS_QUERY, {
      skip: !(open && userId),
      variables: {filter: '%%'},
    },
  )
  const isLoading = (
    (merchantCustomerUsersQueryNetworkStatus === NetworkStatus.loading) &&
    (internalChatsQueryNetworkStatus === NetworkStatus.loading)
  )
  const debounceGetChats = useMemo(
    () => debounce((filter_: string) => {
      refetchInternalChats({filter: filter_, userId})
      refetchMerchantCustomerUsers({filter: filter_})
    }, 500),
    [refetchInternalChats, refetchMerchantCustomerUsers, userId],
  )
  const [
    selectedInternalChatIds,
    setSelectedInternalChatIds,
  ] = useState(new Set<string>())
  const onToggleIsInternalChatSelected = useCallback(
    (chatId: string) => setSelectedInternalChatIds(p => {
      const set = new Set(p)
      set.has(chatId) ? set.delete(chatId) : set.add(chatId)
      return set
    }),
    []
  )
  const [
    selectedCustomerUserWhatsappPhoneNumbers,
    setSelectedCustomerUserWhatsappPhoneNumbers,
  ] = useState(new Set<string>())
  const onToggleIsCustomerUserWhatsappPhoneNumberSelected = useCallback(
    (phoneNumber: string) => setSelectedCustomerUserWhatsappPhoneNumbers(p => {
      const set = new Set(p)
      set.has(phoneNumber) ? set.delete(phoneNumber) : set.add(phoneNumber)
      return set
    }),
    []
  )
  const {
    data: {chat_messages: [{
      chatMessageFileAttachments: [attachment] = [],
      text: initialText,
    } = {}] = []} = {},
  } = useQuery<QueryRoot['chat_messages']>(
    CHAT_MESSAGE_FILE_ATTACHMENTS_QUERY,
    {
      skip: (selectedChatMessages.size !== 1),
      variables: {chatMessageId: selectedChatMessages.keys().next().value},
    },
  )
  const [previewText, setPreviewText] = useState<string>()
  const [
    forwardChatMessages, {loading: isForwardingChatMessages},
  ] = useMutation<MutationRoot['forwardChatMessages']>(
    FORWARD_CHAT_MESSAGES_MUTATION,
    {
      onCompleted({forwardChatMessages}) {
        notify(
          'chat.notifications.forwarded',
          {messageArgs: {smart_count: forwardChatMessages.length, type: 'info'}},
        )
        setFilter('')
        setSelectedInternalChatIds(new Set())
        setSelectedCustomerUserWhatsappPhoneNumbers(new Set())
        onUnsetBulkAction()
        onClose()
        const forwardedTo = Object.entries(
          forwardChatMessages.reduce(
            (chats, chat) => ({
              ...chats,
              [chat.chatMessage?.chat.id as string]:
                chat.chatMessage?.chat.channelId,
            }),
            {},
          )
        )
        if (forwardedTo.length === 1) {
          // Messages were forwarded to only one chat; redirect to that chat.
          const [chatId, channelId] = forwardedTo[0]
          navigate(channelId ? `/inbox/${channelId}/${chatId}` : `/team-chat/${chatId}`)
        }
      },
      variables: {
        channelId,
        chatMessageIds: selectedChatMessages.keys().toArray(),
        customerUserWhatsappPhoneNumbers: Array.from(
          selectedCustomerUserWhatsappPhoneNumbers,
        ),
        internalChatIds: Array.from(selectedInternalChatIds),
        ...(attachment && {text: previewText}),
      },
    }
  )
  const onDialogClose = useCallback(
    () => {
      if (isForwardingChatMessages) return
      setFilter('')
      setSelectedInternalChatIds(new Set())
      setSelectedCustomerUserWhatsappPhoneNumbers(new Set())
      onClose()
    },
    [isForwardingChatMessages, onClose],
  )
  useEffect(() => {
    if (
      !userId || !open || isLoadingInternalChats || isLoadingMerchantCustomerUsers
    ) return
    debounceGetChats(
      `%${filter.trim().replaceAll(/\s+/g, '%')}%`
    )
  }, [
    debounceGetChats,
    filter,
    open,
    isLoadingInternalChats,
    isLoadingMerchantCustomerUsers,
    userId,
  ])
  useEffect(() => {open && setPreviewText(initialText)}, [initialText, open])
  return (
    <ExtendedDialog
      className={styles.dialog}
      onClose={onDialogClose}
      open={open}
      scroll="paper"
    >
      <DialogTitle>
        {translate('dialogs.forwardChatMessages.title')}
      </DialogTitle>
      <DialogCloseButton disabled={isForwardingChatMessages} onClick={onDialogClose} />
      <DialogContent>
        <OutlinedInput
          size="small"
          className={styles.searchInput}
          endAdornment={
            <SearchIcon color="disabled"/>
          }
          onChange={e => setFilter(e.target.value)}
          value={filter}
        />
        <div className={styles.chatList}>
          {isLoading && (
            <div className={styles.loading}>
              <CircularProgress color="secondary" />
            </div>
          )}
          {(!isLoading && !(internalChats.length || merchantCustomerUsers.length)) && (
            <Typography color="secondary" width="full" textAlign="center">
              {translate('dialogs.forwardChatMessages.emptyChatsAndContacts')}
            </Typography>
          )}
          {(!isLoading && !!internalChats.length) && (
            <>
              <Typography className={styles.chatListTitle} color="info">
                {translate('dialogs.forwardChatMessages.teamChats')}
              </Typography>
              {internalChats.map(chat => (
                <div className={styles.chat} key={chat.id}>
                  <Checkbox
                    onClick={() => onToggleIsInternalChatSelected(chat.id)}
                    checked={selectedInternalChatIds.has(chat.id)}
                  />
                  <Typography>
                    {
                      chat.name ||
                      chat.chatUsers
                        .map(cu => userDisplayName({
                          merchantUser: cu.user.merchantUser,
                        }))
                        .join(", ")
                    }
                  </Typography>
                </div>
              ))}
            </>
          )}
          {(!isLoading && !!merchantCustomerUsers.length) && (
            <>
              <Typography className={styles.chatListTitle} color="info">
                {translate('dialogs.forwardChatMessages.contacts')}
              </Typography>
              {merchantCustomerUsers.map(m => (
                <div className={styles.chat} key={m.whatsappPhoneNumber}>
                  <Checkbox
                    onClick={() => {
                      onToggleIsCustomerUserWhatsappPhoneNumberSelected(
                        m.whatsappPhoneNumber,
                      )
                    }}
                    checked={selectedCustomerUserWhatsappPhoneNumbers.has(
                      m.whatsappPhoneNumber,
                    )}
                  />
                  <Typography>
                    {userDisplayName({
                      customerUser: {
                        ...m.customerUser,
                        merchantCustomerUsers: [m],
                      },
                    })}
                  </Typography>
                </div>
              ))}
            </>
          )}
        </div>
      </DialogContent>
      {(
        selectedInternalChatIds.size ||
        selectedCustomerUserWhatsappPhoneNumbers.size ||
        attachment
      ) && (
        <DialogActions>
          {attachment && (
            <div className={styles.chatMessagePreview}>
              <img
                alt={attachment.filename}
                loading="eager"
                src={getFilesApiUrl(
                  attachment.thumbnailMediaKey ?? attachment.key
                )}
              />
              <TextField
                fullWidth
                multiline
                sx={{textarea: {color: previewText === initialText ? 'grey' : 'inherit'}}}
                onChange={e => setPreviewText(e.target.value)}
                value={previewText}
              />
            </div>
          )}
          <Button
            disabled={(
              isForwardingChatMessages ||
              !(
                selectedInternalChatIds.size ||
                selectedCustomerUserWhatsappPhoneNumbers.size
              )
            )}
            onClick={() => forwardChatMessages()}
            color="primary"
            variant="contained"
          >
            {isForwardingChatMessages ?
              <CircularProgress size={16} /> :
              translate('actions.send')
            }
          </Button>
        </DialogActions>
      )}
    </ExtendedDialog>
  )
}

const INTERNAL_CHATS_QUERY = gql`
  query($userId: uuid!, $filter: String!){
    chats(
      limit: 10
      order_by: {lastActivityTimestamp: desc_nulls_last}
      where: {
        isProductNews: {_eq: False}
        type: {_eq: INTERNAL}
        _or: [
          {name: {_ilike: $filter}}
          {
            chatUsers: {
              user: {
                merchantUser: {
                  _or: [
                    {firstName: {_ilike: $filter}}
                    {lastName: {_ilike: $filter}}
                  ]
                }
              }
            }
          }
        ]
      }
    ){
      chatUsers(where: {userId: {_neq: $userId}}){
        user{id merchantUser{firstName id lastName}}
      }
      id
      name
    }
  }
`

const MERCHANT_CUSTOMER_USERS_QUERY = gql`
  query UniqueQuery($filter: String!){
    merchant_customer_users(
      limit: 10
      order_by: {firstName: asc}
      where: {
        _or: [
          {fullText: {_ilike: $filter}}
          {role: {_ilike: $filter}}
          {whatsappPhoneNumber: {_ilike: $filter}}
        ]
      }
    ) {
      customerUser {
        id
        user{id}
        whatsappDisplayName
        whatsappPhoneNumber
      }
      id
      firstName
      lastName
      whatsappPhoneNumber
    }
  }
`

const CHAT_MESSAGE_FILE_ATTACHMENTS_QUERY = gql`
  query ($chatMessageId: uuid!) {
    chat_messages(
      where: {
        chatMessageFileAttachments_aggregate: {count: {predicate: {_eq: 1}}}
        id: {_eq: $chatMessageId}
      }
    ){chatMessageFileAttachments{key thumbnailMediaKey} text}
  }
`

const FORWARD_CHAT_MESSAGES_MUTATION = gql`
  mutation (
    $channelId: uuid!
    $chatMessageIds: [uuid!]!
    $customerUserWhatsappPhoneNumbers: [String]
    $internalChatIds: [uuid]
    $text: String
  ) {
    forwardChatMessages(
      channelId: $channelId
      chatMessageIds: $chatMessageIds
      customerUserWhatsappPhoneNumbers: $customerUserWhatsappPhoneNumbers
      internalChatIds: $internalChatIds
      text: $text
    ){chatMessage{chat{channelId id}}}
  }
`

const useStyles = makeStyles(theme => ({
  chat: {
    '& .MuiCheckbox-root': {
      padding: 0,
    },
    alignItems: 'center',
    display: 'flex',
    gap: theme.remSpacing(1.5),
    padding: `${theme.remSpacing(1)} 0`,
  },
  chatList: {
    [theme.breakpoints.up('lg')]: {
      height: 'calc(100% - 50.8px)',
      overflowY: 'scroll',
    },
  },
  chatListTitle: {
    margin: `${theme.remSpacing(2)} 0`,
  },
  chatMessagePreview: {
    '& .MuiFormControl-root': {
      margin: 0,
    },
    '& .MuiInputBase-input': {
      background: 'transparent',
      border: '0',
    },
    '& .MuiInputBase-root': {
      borderRadius: theme.remSpacing(1),
      boxShadow: 'none',
      padding: '0',
    },
    '& > img': {
      borderRadius: theme.remSpacing(0.5),
      height: theme.remSpacing(8),
      objectFit: 'cover',
      width: theme.remSpacing(8),
    },
    display: 'flex',
    gap: theme.remSpacing(1),
    justifyContent: 'space-between',
    marginBottom: theme.remSpacing(1),
    width: '100%',
  },
  dialog: {
    [theme.breakpoints.up('lg')]: {
      '& .MuiDialogContent-root': {
        height: theme.remSpacing(48),
        overflowY: 'hidden',
      },
      '& .MuiPaper-root': {
        width: theme.remSpacing(54),
      },
    },
    '& .MuiDialogActions-root': {
      background: theme.palette.background.default,
      padding: `${theme.remSpacing(2)} ${theme.remSpacing(3)}`,
    },
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: `${theme.remSpacing(1)} 0`,
  },
  searchInput: {
    '&.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${theme.palette.info.main}`,
      },
    },
    borderRadius: theme.remSpacing(0.5),
    boxShadow: 'box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.10)',
    marginBottom: theme.remSpacing(3),
    minHeight: theme.remSpacing(1),
    width: '100%',
  },
}))

interface ForwardChatMessagesDialogProps {
  onClose: () => void
  open: boolean
}

export default ForwardChatMessagesDialog
